import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Check, X, ChevronDown, ChevronUp, Info } from "lucide-react";
import { EPISTEMIC } from "@/lib/i18n";
import { useApp } from "@/context/AppContext";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";

export function PageHeader({ eyebrow, title, desc, children }) {
  return (
    <div className="flex flex-wrap items-end gap-4 mb-6 fade-up">
      <div className="min-w-0">
        <div className="eyebrow mb-2">{eyebrow}</div>
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight">{title}</h1>
        {desc && <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{desc}</p>}
      </div>
      <div className="ml-auto flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function EpistemicBadge({ value }) {
  const { lang } = useApp();
  const e = EPISTEMIC[value] || EPISTEMIC.unverified;
  return <span data-testid="epistemic-badge" className={`text-[10px] font-mono px-1.5 py-0.5 rounded border whitespace-nowrap ${e.cls}`}>{e[lang]}</span>;
}

export function Reliability({ value }) {
  const c = value >= 80 ? "bg-emerald-500" : value >= 55 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2" title={`Fiabilité ${value}/100`}>
      <div className="w-16 h-1.5 rounded bg-muted overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${value}%` }} /></div>
      <span className="text-[10px] font-mono text-muted-foreground">{value}</span>
    </div>
  );
}

export function KV({ data, testid }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!entries.length) return <div className="text-xs text-muted-foreground">—</div>;
  return (
    <div className="grid grid-cols-[minmax(110px,180px)_1fr] gap-x-3 gap-y-1 text-xs" data-testid={testid}>
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <div className="font-mono text-muted-foreground truncate">{k}</div>
          <div className="font-mono break-all">{typeof v === "object" ? <pre className="whitespace-pre-wrap">{JSON.stringify(v, null, 1).slice(0, 2000)}</pre> : String(v)}</div>
        </div>
      ))}
    </div>
  );
}

const PIVOT_KEYS = ["handle", "github", "twitter", "domain", "blog", "website", "reg", "hex"];

export function ResultCard({ r, onReview, idx = 0 }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(r.status || "pending");
  const { can } = useApp();
  const review = async (s) => {
    try {
      await api.patch(`/results/${r.id}`, { status: s, comment: "" });
      setStatus(s);
      onReview?.(r.id, s);
      toast.success(s === "accepted" ? "Hypothèse acceptée" : s === "rejected" ? "Hypothèse rejetée" : "Remis en attente");
    } catch (e) { toast.error("Revue impossible"); }
  };
  const pivots = PIVOT_KEYS.filter((k) => r.data?.[k] && typeof r.data[k] === "string").map((k) => r.data[k].replace(/^https?:\/\//, "").replace(/\/$/, ""));
  return (
    <div data-testid={`result-card-${r.id}`} style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
      className={`panel p-4 fade-up transition-colors ${status === "rejected" ? "opacity-50" : ""} ${status === "accepted" ? "border-emerald-500/50" : "hover:border-primary/40"}`}>
      <div className="flex items-start gap-3">
        {r.data?.avatar && <img src={r.data.avatar} alt="" className="w-10 h-10 rounded object-cover border" referrerPolicy="no-referrer" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <EpistemicBadge value={r.epistemic} />
            <span className="text-[10px] font-mono uppercase text-muted-foreground">{r.info_type}</span>
            <span className="text-[10px] font-mono text-primary">{r.source}</span>
          </div>
          <div className="font-semibold text-sm break-words" data-testid="result-title">{r.title}</div>
          <div className="text-xs text-muted-foreground mt-1 break-words">{r.summary}</div>
          <div className="flex items-start gap-1.5 mt-2 text-[11px] text-muted-foreground/90">
            <Info size={12} className="mt-0.5 shrink-0 text-primary" /> <span>{r.match_explanation}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Reliability value={r.reliability} />
          {r.url && <a href={r.url} target="_blank" rel="noreferrer" data-testid="result-source-link" className="text-[11px] flex items-center gap-1 text-primary hover:underline">Source <ExternalLink size={11} /></a>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t text-[10px] font-mono text-muted-foreground">
        <span>Découvert : {fmtDate(r.discovered_at)}</span><span>·</span><span>Vérifié : {fmtDate(r.verified_at)}</span>
        {pivots.slice(0, 3).map((p) => (
          <Link key={p} to={`/search?q=${encodeURIComponent(p)}`} className="px-1.5 py-0.5 rounded border hover:border-primary hover:text-primary" data-testid="result-pivot-link">pivot → {p.slice(0, 30)}</Link>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {can("admin", "analyst") && r.id && (<>
            <button data-testid="result-accept-button" onClick={() => review(status === "accepted" ? "pending" : "accepted")} className={`h-6 px-2 rounded border flex items-center gap-1 ${status === "accepted" ? "bg-emerald-500/20 text-emerald-500" : "hover:text-emerald-500"}`}><Check size={11} />Accepter</button>
            <button data-testid="result-reject-button" onClick={() => review(status === "rejected" ? "pending" : "rejected")} className={`h-6 px-2 rounded border flex items-center gap-1 ${status === "rejected" ? "bg-rose-500/20 text-rose-500" : "hover:text-rose-500"}`}><X size={11} />Rejeter</button>
          </>)}
          {Object.keys(r.data || {}).length > 0 && (
            <button data-testid="result-details-toggle" onClick={() => setOpen(!open)} className="h-6 px-2 rounded border flex items-center gap-1 hover:text-foreground">Données {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</button>
          )}
        </div>
      </div>
      {open && <div className="mt-3 p-3 rounded bg-background/60 border"><KV data={r.data} /></div>}
    </div>
  );
}

export function StatusDot({ status }) {
  const c = { ok: "bg-emerald-500", running: "bg-amber-400 animate-pulse", error: "bg-rose-500", timeout: "bg-orange-500" }[status] || "bg-muted-foreground";
  return <span className={`inline-block w-2 h-2 rounded-full ${c}`} />;
}

export function Empty({ children }) {
  return <div className="panel p-10 text-center text-sm text-muted-foreground grid-bg" data-testid="empty-state">{children}</div>;
}
